import { useState } from "react";
import { VisitCard } from "@/components/VisitCard";
import { Button } from "@/components/ui/button";
import { useLeadVisit, useLeadVisitEvents } from "@/hooks/useLeadVisit";
import { useCancelVisit, useRescheduleVisit } from "@/hooks/useVisits";
import { CancelVisitDialog } from "./CancelVisitDialog";
import { SlotPickerDialog } from "./SlotPickerDialog";
import { VisitEventsList } from "./VisitEventsList";
import { formatVisitDateTime } from "./visit-status";

type ModalState = "reschedule" | "cancel" | null;

/**
 * The lead's visit block for LeadDetail: card + latest events + actions.
 * Self-hides when the lead has no visit and no visit history.
 */
export function LeadVisitSection({ leadId }: { leadId: string }): React.JSX.Element | null {
  const { data: visitData } = useLeadVisit(leadId);
  const { data: eventsData } = useLeadVisitEvents(leadId);
  const [modal, setModal] = useState<ModalState>(null);
  const rescheduleVisit = useRescheduleVisit();
  const cancelVisit = useCancelVisit();

  const visit = visitData?.visit ?? null;
  const events = eventsData?.events ?? [];
  if (!visit && events.length === 0) return null;

  const canAct =
    visit !== null &&
    visit.status === "scheduled" &&
    new Date(visit.startsAt).getTime() > Date.now();
  const closeModal = (): void => setModal(null);

  return (
    <div className="flex flex-col gap-2">
      <p className="text-[10px] font-semibold uppercase tracking-widest text-ink-300">
        Visita
      </p>
      {visit && <VisitCard visit={visit} />}
      {canAct && visit && (
        <div className="flex gap-2">
          <Button variant="outline" size="sm" onClick={() => setModal("reschedule")}>
            Reprogramar
          </Button>
          <Button
            variant="ghost"
            size="sm"
            className="text-red-600 hover:bg-red-50"
            onClick={() => setModal("cancel")}
          >
            Cancelar
          </Button>
        </div>
      )}
      <VisitEventsList events={events} limit={5} />

      {modal === "reschedule" && visit && (
        <SlotPickerDialog
          open
          onOpenChange={(open) => {
            if (!open) closeModal();
          }}
          excludeBookingId={visit.id}
          isSaving={rescheduleVisit.isPending}
          onSelect={(startsAt) =>
            rescheduleVisit.mutate(
              { bookingId: visit.id, body: { startsAt } },
              { onSuccess: closeModal },
            )
          }
        />
      )}
      {modal === "cancel" && visit && (
        <CancelVisitDialog
          open
          onOpenChange={(open) => {
            if (!open) closeModal();
          }}
          visitLabel={formatVisitDateTime(visit.startsAt)}
          isSaving={cancelVisit.isPending}
          onConfirm={(reason) =>
            cancelVisit.mutate(
              { bookingId: visit.id, body: reason ? { reason } : {} },
              { onSuccess: closeModal },
            )
          }
        />
      )}
    </div>
  );
}
